import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Drop-in replacement for `<input type="password">` used purely for visual
 * masking (CNP/phone/email eye-toggle fields), NOT real passwords.
 *
 * Browsers attach their credential-manager heuristics (autofill, "use saved
 * password" suggestions, etc.) to any `type="password"` field, regardless of
 * `autocomplete` hints - Chrome/Firefox mostly respect `autocomplete="new-
 * password"`, but Safari is inconsistent. The only fully reliable fix is to
 * never use `type="password"` at all.
 *
 * This component always renders a native `type="text"` input (so no browser
 * ever treats it as a credential field) and fakes the masked look itself by
 * displaying a bullet character per real character, while keeping the real
 * value in sync via the `beforeinput` event (which reports the exact edit -
 * insert/delete/paste - before the browser applies it to the DOM). We always
 * compute the resulting value ourselves and take over rendering (masked or
 * not), rather than ever reading the edit result back from the DOM.
 */
@Component({
  selector: 'app-masked-input',
  template: `
    <input
      #inputRef
      type="text"
      [id]="id"
      [class]="className"
      [placeholder]="placeholder"
      [attr.inputmode]="inputMode"
      [disabled]="disabled"
      autocomplete="off"
      (beforeinput)="onBeforeInput($event)"
      (blur)="onBlur()"
    />
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MaskedInputComponent),
      multi: true,
    },
  ],
})
export class MaskedInputComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @Input() id: string | null = null;
  @Input() placeholder = '';
  @Input() inputMode: string | null = null;
  @Input() className = 'form-control';
  @Input() masked = true;
  @Input() maxLength: number | null = null;

  @ViewChild('inputRef', { static: true }) inputRef: ElementRef<HTMLInputElement>;

  value = '';
  disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.masked && !changes.masked.firstChange) {
      this.renderDisplayValue();
    }
  }

  ngAfterViewInit(): void {
    this.renderDisplayValue();
  }

  writeValue(value: string): void {
    this.value = value ?? '';
    this.renderDisplayValue();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onBlur(): void {
    this.onTouched();
  }

  /**
   * Intercepts every edit via `beforeinput` (fired before the browser
   * touches the DOM) and always takes over applying it ourselves - computing
   * the resulting real value from the pre-edit selection/insert/delete info,
   * then rendering the result (masked as bullets, or plain) back into the
   * DOM, for both masked and unmasked modes.
   *
   * Earlier versions let the browser apply the edit natively while unmasked
   * and asynchronously read the result back out of the DOM - that created a
   * race with Angular's own writeValue()/change-detection cycle, which could
   * re-render the input from a stale `this.value` before the async read ran,
   * silently reverting the edit (e.g. selecting one character and replacing
   * it, then toggling the eye icon, revealed the pre-edit value). Always
   * computing synchronously here avoids that race entirely.
   */
  onBeforeInput(event: InputEvent): void {
    const inputEl = event.target as HTMLInputElement;
    const start = inputEl.selectionStart ?? this.value.length;
    const end = inputEl.selectionEnd ?? start;
    let newValue: string;
    let cursorPos: number;

    switch (event.inputType) {
      case 'deleteContentBackward':
        if (start === end) {
          cursorPos = Math.max(0, start - 1);
          newValue = this.value.slice(0, cursorPos) + this.value.slice(end);
        } else {
          cursorPos = start;
          newValue = this.value.slice(0, start) + this.value.slice(end);
        }
        break;
      case 'deleteContentForward':
        cursorPos = start;
        newValue = start === end
          ? this.value.slice(0, start) + this.value.slice(start + 1)
          : this.value.slice(0, start) + this.value.slice(end);
        break;
      default: {
        // insertText, insertFromPaste, insertFromDrop, insertReplacementText, ...
        const insertedText = event.data ?? '';
        newValue = this.value.slice(0, start) + insertedText + this.value.slice(end);
        cursorPos = start + insertedText.length;
        if (this.maxLength !== null && newValue.length > this.maxLength) {
          newValue = newValue.slice(0, this.maxLength);
          cursorPos = Math.min(cursorPos, this.maxLength);
        }
        break;
      }
    }

    this.value = newValue;
    this.onChange(this.value);

    // Always take over rendering ourselves (both masked and unmasked) rather
    // than relying on the browser's default action - this keeps enforcement
    // (e.g. maxLength truncation) and cursor placement consistent in both
    // modes, and avoids any timing gap between the real value and the DOM.
    event.preventDefault();
    this.renderDisplayValue(cursorPos);
  }

  private renderDisplayValue(cursorPos?: number): void {
    const inputEl = this.inputRef?.nativeElement;
    if (!inputEl) {
      return;
    }
    inputEl.value = this.masked ? '\u2022'.repeat(this.value.length) : this.value;
    if (cursorPos !== undefined) {
      inputEl.setSelectionRange(cursorPos, cursorPos);
    }
  }
}
